import { useCallback, useEffect, useState } from 'react';
import AppShell from '../../components/AppShell';
import Button from '../../components/Button';
import Callout from '../../components/Callout';
import Field from '../../components/Field';
import { api, type AccessRequest } from '../../lib/api';
import { getVertical } from '../../data/verticals';

export default function AdminRequests() {
  const [tab, setTab] = useState<'pending' | 'approved' | 'declined'>('pending');
  const [rows, setRows] = useState<AccessRequest[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [reason, setReason] = useState<Record<string, string>>({});
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { requests } = await api.adminRequests(tab);
    setRows(requests);
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (r: AccessRequest) => {
    setBusy(r.id);
    setNote(null);
    try {
      const out = await api.approveRequest(r.id);
      setNote(
        out.inviteUrl
          ? `Approved. Email is not configured, so send this link to ${r.email} yourself: ${out.inviteUrl}`
          : `Approved. A sign-in link is on its way to ${r.email}.`,
      );
      await load();
    } finally {
      setBusy(null);
    }
  };

  const decline = async (r: AccessRequest) => {
    setBusy(r.id);
    try {
      await api.declineRequest(r.id, reason[r.id]);
      await load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <AppShell action={<span className="t-meta text-grey-600">Access requests</span>}>
      <p className="t-eyebrow text-green-600">Admin</p>
      <h1 className="t-h1 mt-3 text-ink">Access requests</h1>

      <div className="mt-8 flex flex-wrap gap-2">
        {(['pending', 'approved', 'declined'] as const).map((t) => (
          <button
            key={t}
            type="button"
            aria-pressed={tab === t}
            onClick={() => setTab(t)}
            className={
              tab === t
                ? 'min-h-tap rounded-full border border-green-600 bg-green-050 px-5 text-[0.95rem] font-semibold text-green-900'
                : 'min-h-tap rounded-full border border-grey-300 bg-white px-5 text-[0.95rem] text-ink hover:bg-grey-050'
            }
          >
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {note && (
        <div className="mt-8">
          <Callout tone="confirm" title="Done">
            <span className="break-all">{note}</span>
          </Callout>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="mt-10">
          <Callout title={`Nothing ${tab}`}>
            {tab === 'pending'
              ? 'No one is waiting. Requests land here as they come in.'
              : `No ${tab} requests yet.`}
          </Callout>
        </div>
      ) : (
        <ul className="mt-10 flex flex-col gap-4">
          {rows.map((r) => (
            <li key={r.id} className="rounded-lg border border-grey-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="t-h3 text-ink">
                  {r.name}
                  {r.org ? ` — ${r.org}` : ''}
                </h2>
                <p className="t-meta text-grey-600">{r.created_at}</p>
              </div>
              <dl className="t-meta mt-3 grid gap-x-8 gap-y-1 text-grey-600 sm:grid-cols-2">
                <div><dt className="inline font-semibold">Email: </dt><dd className="inline">{r.email}</dd></div>
                <div><dt className="inline font-semibold">Phone: </dt><dd className="inline">{r.phone || '—'}</dd></div>
                <div>
                  <dt className="inline font-semibold">Building: </dt>
                  <dd className="inline">{r.vertical ? (getVertical(r.vertical)?.label ?? r.vertical) : '—'}</dd>
                </div>
              </dl>
              {r.message && <p className="t-body measure mt-4 text-ink">{r.message}</p>}
              {r.decline_reason && (
                <p className="t-meta mt-3 text-grey-600">Declined because: {r.decline_reason}</p>
              )}

              {tab === 'pending' && (
                <div className="mt-6 flex flex-col gap-4">
                  <Field
                    id={`reason-${r.id}`}
                    label="Reason, if declining"
                    hint="Optional. It goes in the email, so keep it kind."
                    value={reason[r.id] ?? ''}
                    onChange={(e) => setReason((s) => ({ ...s, [r.id]: e.target.value }))}
                  />
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => void approve(r)} disabled={busy === r.id}>
                      {busy === r.id ? 'Working…' : 'Approve and send a link'}
                    </Button>
                    <Button variant="secondary" onClick={() => void decline(r)} disabled={busy === r.id}>
                      Decline
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}
